import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addInventoryItem, getProductTypes } from '../../api/inventory';
import { DeliveryMethod, ProductInventoryItem } from 'api/inventory-types';

const AddStock = () => {
    const navigate = useNavigate();
    const { productId } = useParams();
    const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>(DeliveryMethod.VIRTUAL_PRODUCT);
    const [dataMode, setDataMode] = useState<'coupon' | 'keyvalue'>('coupon');
    const [couponCode, setCouponCode] = useState('');
    const [formData, setFormData] = useState({
        amount: '1',
        instanceData: '{}'
    });
    const [error, setError] = useState('');
    const [jsonError, setJsonError] = useState('');

    useEffect(() => {
        const loadProduct = async () => {
            const products = await getProductTypes();
            const product = products.find(p => p.id === productId);
            if (product) {
                setDeliveryMethod(product.deliveryMethod as DeliveryMethod);
            }
        };
        loadProduct();
    }, [productId]);

    const validateJson = (jsonString: string): boolean => {
        try {
            JSON.parse(jsonString);
            setJsonError('');
            return true;
        } catch (err) {
            setJsonError('Invalid JSON format');
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        let instanceData: Record<string, any>;
        if (dataMode === 'coupon') {
            if (couponCode.length !== 0) {
                instanceData = { couponCode };
            } else {
                instanceData = {};
            }
        } else if (!validateJson(formData.instanceData)) {
            return;
        } else {
            instanceData = JSON.parse(formData.instanceData);
        }

        try {
            const item = new ProductInventoryItem(
                crypto.randomUUID(),
                parseInt(formData.amount), 
                productId!, 
                deliveryMethod,
                instanceData,
                new Date());

            const response = await addInventoryItem(item);

            if (response.status === 200) {
                navigate('/admin/products');
            } else {
                setError('Failed to add inventory items');
            }
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'instanceData') {
            validateJson(value);
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-8">
            <h2 className="text-2xl font-bold mb-6">Add Stock Items</h2>
            <div className="bg-gray-100 border border-gray-300 rounded p-4 mb-6">
                <p className="text-sm text-gray-600">Product ID: <span className="font-mono">{productId}</span></p>
            </div>
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex space-x-4 mb-4">
                    <button
                        type="button"
                        onClick={() => setDataMode('coupon')}
                        className={`px-4 py-2 rounded ${dataMode === 'coupon' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Coupon Code
                    </button>
                    <button
                        type="button"
                        onClick={() => setDataMode('keyvalue')}
                        className={`px-4 py-2 rounded ${dataMode === 'keyvalue' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Key-Value Pairs
                    </button>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Amount
                    </label>
                    <input
                        type="number"
                        name="amount"
                        min="1"
                        required
                        value={formData.amount}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                        placeholder="1"
                    />
                </div>

                <div>
                    {dataMode === 'coupon' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Coupon Code
                            </label>
                            <input
                                type="text"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                                className="w-full border border-gray-300 rounded-md shadow-sm p-2"
                                placeholder="Enter coupon code"
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Instance Data (Key-Value Pairs)
                            </label>
                            {Object.keys(JSON.parse(formData.instanceData || '{}')).map((key, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={key}
                                        onChange={(e) => {
                                            const oldData = JSON.parse(formData.instanceData || '{}');
                                            const value = oldData[key];
                                            delete oldData[key];
                                            oldData[e.target.value] = value;
                                            setFormData({
                                                ...formData,
                                                instanceData: JSON.stringify(oldData)
                                            });
                                        }}
                                        className="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
                                        placeholder="Key"
                                    />
                                    <input
                                        type="text"
                                        value={JSON.parse(formData.instanceData || '{}')[key]}
                                        onChange={(e) => {
                                            const data = JSON.parse(formData.instanceData || '{}');
                                            data[key] = e.target.value;
                                            setFormData({
                                                ...formData,
                                                instanceData: JSON.stringify(data)
                                            });
                                        }}
                                        className="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
                                        placeholder="Value"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const data = JSON.parse(formData.instanceData || '{}');
                                            delete data[key];
                                            setFormData({
                                                ...formData,
                                                instanceData: JSON.stringify(data)
                                            });
                                        }}
                                        className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    const data = JSON.parse(formData.instanceData || '{}');
                                    data[`key${Object.keys(data).length + 1}`] = '';
                                    setFormData({
                                        ...formData,
                                        instanceData: JSON.stringify(data)
                                    });
                                }}
                                className="mt-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                            >
                                Add Key-Value Pair
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/products')}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        disabled={!!jsonError}
                    >
                        Add Stock
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddStock; 